import React from 'react';

import { NotAuthorizedView, NotFound } from '@gasbi/shared';
import { DialogType } from '@gasbi/shared/web/components/Dialog';
import dayjs from 'dayjs';
import { Navigate, createBrowserRouter } from 'react-router-dom';

import { ISO_DATE_FORMAT } from '@/forms/utils/formatDate';
import Pages, { getOperatingGasRoutesURL } from '@/pages';
import { actualExpensesDetailsRoutes } from '@/pages/ActualExpensesMeteringPointDetails/routes';
import { actualIncomeDetailsRoutes } from '@/pages/ActualIncomeDetails/routes';
import {
  commercialMeteringPointDetailsRoutes,
  commercialMeteringPointsNodeRoutes,
} from '@/pages/CommercialMeteringPointDetails/routes';
import { contractDetailsRoutes } from '@/pages/ContractDetails/routes';
import { gasDistributionStationRoutes } from '@/pages/GasDistributionStationDetails/routes';
import { gasDistributionStationGasThreadRoutes } from '@/pages/GasDistributionStationGasThread/routes';
import { gasThreadRoutes } from '@/pages/GasThreadDetails/routes';
import { meteringPointActDetailsRoutes } from '@/pages/MeteringPointActDetails/routes';
import {
  meteringPointsNodeRoutes,
  meteringPointsRoutes,
} from '@/pages/MeteringPointDetails/routes';
import { organizationRoutes } from '@/pages/MyOrganization/routes';
import { sectorDetailsRoutes } from '@/pages/SectorDetails/routes';
import { violationActDetailsRoutes } from '@/pages/ViolationActDetails/routes';
import type { AppRouteObject } from '@/routeTypes';

import { makeAuditRoute } from '@components/Audit/utils';
import { UserPermissions } from '@components/Permissions/userPermission';

import CommercialMeteringPointBalanceActDetails from './pages/CommercialMeteringPointBalanceActDetails';

const Error = React.lazy(
  () => import('@gasbi/shared/web/components/ErrorBoundary/ErrorView'),
);

const CommercialMeteringPointBalanceActForm = React.lazy(
  () => import('@/pages/CommercialMeteringPointBalanceActForm'),
);

const Router = React.lazy(() => import('@/pages/Router'));

const AnnualOrderedCapacity = React.lazy(
  () => import('@/pages/AnnualOrderedCapacity'),
);

const OperationalExpenses = React.lazy(
  () => import('@/pages/OperationalExpenses'),
);
const OperationalExpenseDetails = React.lazy(
  () => import('@/pages/OperationalExpenseDetails'),
);
const OperationalExpenseAudit = React.lazy(
  () => import('@/pages/OperationalExpenseAudit'),
);
const OperationalExpensesForm = React.lazy(
  () => import('@/forms/OperationalExpensesForm'),
);
const CommercialMeteringPointsView = React.lazy(
  () => import('@/pages/CounterpartDetails/views/CommercialMeteringPoints'),
);

const CounterpartInformation = React.lazy(
  () => import('@/pages/CounterpartDetails/views/Information'),
);
const CounterpartDetails = React.lazy(
  () => import('@/pages/CounterpartDetails'),
);

const AuthCallback = React.lazy(() => import('@/pages/auth/AuthCallback'));
const ChangePassword = React.lazy(() => import('@/pages/auth/ChangePassword'));

const GasDistributionStations = React.lazy(
  () => import('@/pages/GasDistributionStations'),
);
const GasDistributionStationDetails = React.lazy(
  () => import('@/pages/GasDistributionStationDetails'),
);
const GasDistributionStationGasThread = React.lazy(
  () => import('@/pages/GasThreadDetails'),
);

const GasThreads = React.lazy(() => import('@/pages/GasThreads'));
const GasThreadDetails = React.lazy(() => import('@/pages/GasThreadDetails'));
const GasThreadMeteringPoints = React.lazy(
  () => import('@/pages/GasThreadMeteringPoints'),
);

const Counterparties = React.lazy(() => import('@/pages/Counterparties'));

const MeteringPoints = React.lazy(() => import('@/pages/MeteringPoints'));
const MeteringPointDetails = React.lazy(
  () => import('@/pages/MeteringPointDetails'),
);
const NodeDetails = React.lazy(() => import('@/pages/NodeDetails'));

const SectorBridges = React.lazy(() => import('@/pages/SectorBridges'));
const SectorBridgeDetails = React.lazy(
  () => import('@/pages/SectorBridgeDetails'),
);
const SectorBridgeAudit = React.lazy(() => import('@/pages/SectorBridgeAudit'));
const SectorBridgeDetailsBridgeActs = React.lazy(
  () => import('@/pages/SectorBridgeDetails/views/BridgeActs'),
);
const SectorBridgeDetailsSectors = React.lazy(
  () => import('@/pages/SectorBridgeDetails/views/Sectors'),
);
const BridgeActs = React.lazy(() => import('@/pages/BridgeActs'));
const BridgeActDetails = React.lazy(() => import('@/forms/BridgeActForm'));

const MeteringDeviceConsumerIndicators = React.lazy(
  () => import('@/pages/MeteringDeviceConsumerIndicators'),
);

const GasRoutes = React.lazy(() => import('@/pages/GasRoutes'));
const GRoutes = React.lazy(() => import('@/pages/GasRoutes/views/Routes'));
const GasRouteDetails = React.lazy(() => import('@/forms/GasRouteForm'));
const OperatingRoutes = React.lazy(
  () => import('@/pages/GasRoutes/views/OperatingRoutes'),
);
const OperatingRouteDetails = React.lazy(
  () => import('@/forms/OperatingGasRouteForm'),
);
const OperatingGasRouteAudit = React.lazy(
  () => import('@/pages/OperatingGasRouteAudit'),
);

const CommercialMeteringPoints = React.lazy(
  () => import('@/pages/CommercialMeteringPoints'),
);
const CommercialMeteringPointDetails = React.lazy(
  () => import('@/pages/CommercialMeteringPointDetails'),
);

const Contracts = React.lazy(() => import('@/pages/Contracts'));
const ContractDetails = React.lazy(() => import('@/pages/ContractDetails'));
const CounterpartContracts = React.lazy(
  () => import('@/pages/CounterpartDetails/views/CounterpartContracts'),
);

const CounterpartRequests = React.lazy(
  () => import('@/pages/CounterpartDetails/views/Requests'),
);
const CounterpartDocuments = React.lazy(
  () => import('@/pages/CounterpartDetails/views/Documents'),
);

const DocumentDetails = React.lazy(
  () => import('@/pages/CounterpartDetails/views/DocumentDetails'),
);

const Sectors = React.lazy(() => import('@/pages/Sectors'));
const SectorDetails = React.lazy(() => import('@/pages/SectorDetails'));

const CommercialMeteringPointOwnerships = React.lazy(
  () => import('@/pages/CommercialMeteringPointOwnerships'),
);
const CommercialMeteringPointOwnershipsFormView = React.lazy(
  () =>
    import('@/pages/CommercialMeteringPointOwnerships/components/CommercialMeteringPointOwnershipsFormView'),
);

const OperationalIncomes = React.lazy(
  () => import('@/pages/OperationalIncomes'),
);
const OperationalIncomeDetails = React.lazy(
  () => import('@/pages/OperationalIncomeDetails'),
);
const OperationalIncomeAudit = React.lazy(
  () => import('@/pages/OperationalIncomeAudit'),
);
const OperationalIncomeCosts = React.lazy(
  () => import('@/forms/OperationalIncomeCostsForm'),
);

const MeteringPointActForm = React.lazy(
  () => import('@/pages/MeteringPointActForm'),
);

const CommercialMeteringPointActForm = React.lazy(
  () => import('@/pages/CommercialMeteringPointActForm'),
);

const MeteringPointActDetails = React.lazy(
  () => import('@/pages/MeteringPointActDetails'),
);

const FormNine = React.lazy(() => import('@/pages/FormNine'));
const FormNineDetails = React.lazy(() => import('@/pages/FormNineDetails'));
const FormNineAudit = React.lazy(() => import('@/pages/FormNineAudit'));

const InfoRequestForm = React.lazy(
  () => import('@/forms/CounterpartRequestsForm/Info'),
);

const MonthlyPCP = React.lazy(() => import('@/pages/MonthlyPCP'));

const MonthlyPCPDetails = React.lazy(() => import('@/pages/MonthlyPCPDetails'));
const MonthlyPCPAudit = React.lazy(() => import('@/pages/MonthlyPCPAudit'));

const HigherCalorificValueValueFormForGasTread = React.lazy(
  () => import('@/forms/HigherCalorificValueValueFormForGasTread'),
);
const HigherCalorificValueValueFormForGDS = React.lazy(
  () => import('@/forms/HigherCalorificValueValueFormForGDS'),
);

const ActualIncomes = React.lazy(() => import('@/pages/ActualIncomes'));

const ActualIncomeDetails = React.lazy(
  () => import('@/pages/ActualIncomeDetails'),
);
const ActualIncomeAudit = React.lazy(() => import('@/pages/ActualIncomeAudit'));

const NoPermissions = React.lazy(() => import('@/pages/NoPermissions'));

const ActualExpensesCounterparties = React.lazy(
  () => import('@/pages/ActualExpensesCounterparties'),
);

const ActualExpensesCounterpartDetails = React.lazy(
  () => import('@/pages/ActualExpensesCounterpartDetails'),
);

const ActualExpensesMeteringPointDetails = React.lazy(
  () => import('@/pages/ActualExpensesMeteringPointDetails'),
);

const OrderedCapacityCounterpartyDetails = React.lazy(
  () => import('@/pages/AnnualOrderedCapacityDetails'),
);

const TechnicalConditionsDocumentForm = React.lazy(
  () => import('@/forms/TechnicalConditionsForm/index'),
);

const CommercialMeteringPointTechnicalConditionsDocumentDetails = React.lazy(
  () =>
    import('@/pages/CommercialMeteringPointTechnicalConditionsDocumentDetails'),
);

const MeteringPointProjectForm = React.lazy(
  () => import('@/pages/MeteringPointProjectForm'),
);

const CommercialMeteringPointProjectDetails = React.lazy(
  () => import('@/pages/CommercialMeteringPointProjectDetails'),
);

const GuaranteeLetterForm = React.lazy(
  () => import('@/forms/GuaranteeLetterForm/index'),
);

const CommercialMeteringPointGuaranteeLetterDetails = React.lazy(
  () => import('@/pages/CommercialMeteringPointGuaranteeLetterDetails'),
);

const CommercialMeteringPointAudit = React.lazy(
  () => import('@/pages/CommercialMeteringPointAudit'),
);

const Reports = React.lazy(() => import('@/pages/Reports'));
const ReportHistory = React.lazy(() => import('@/pages/ReportHistory'));
const ReportConstructor = React.lazy(() => import('@/pages/ReportConstructor'));

const ViolationActForm = React.lazy(() => import('@/pages/ViolationActForm'));
const ViolationActDetails = React.lazy(
  () => import('@/pages/ViolationActDetails'),
);

const OrderedCapacityExcesses = React.lazy(
  () => import('@/pages/OrderedCapacityExcesses'),
);
const OrderedCapacityExcessDetails = React.lazy(
  () => import('@/pages/OrderedCapacityExcessDetails'),
);

const NaturalGasDistribution = React.lazy(
  () => import('@/pages/NaturalGasDistribution'),
);

const MonitoringDashboard = React.lazy(
  () => import('@/pages/MonitoringDashboard'),
);

const ExpiredDevices = React.lazy(() => import('@/pages/ExpiredDevices'));
const MeteringPointsToDisconnect = React.lazy(
  () => import('@/pages/MeteringPointsToDisconnect'),
);
const MeteringPointsToVerify = React.lazy(
  () => import('@/pages/MeteringPointsToVerify'),
);

const ConsumerDocuments = React.lazy(() => import('@/pages/ConsumerDocuments'));

const FormTen = React.lazy(() => import('@/pages/FormTen'));
const FormTenDetails = React.lazy(() => import('@/pages/FormTenDetails'));

const OrderedCapacityFinalExcess = React.lazy(
  () => import('@/pages/OrderedCapacityFinalExcess'),
);
const OrderedCapacityFinalExcessDetails = React.lazy(
  () => import('@/pages/OrderedCapacityFinalExcessDetails'),
);

const MeteringPointAudit = React.lazy(
  () => import('@/pages/MeteringPointAudit'),
);

const ContractAudit = React.lazy(() => import('@/pages/ContractAudit'));
const CounterpartyAudit = React.lazy(() => import('@/pages/CounterpartyAudit'));

export const routes: AppRouteObject[] = [
  ...organizationRoutes,
  {
    path: Pages.CHANGE_PASSWORD,
    element: <ChangePassword />,
  },
  {
    path: Pages.GAS_DISTRIBUTION_STATIONS,
    element: <GasDistributionStations />,
    handle: {
      keepAlive: true,
      permissions: UserPermissions.GasDistributionStationGDSRead,
    },
  },
  {
    path: `${Pages.GAS_DISTRIBUTION_STATIONS}/:stationId`,
    element: <GasDistributionStationDetails />,
    children: gasDistributionStationRoutes,
    handle: {
      permissions: UserPermissions.GasDistributionStationGDSRead,
    },
  },
  {
    path: `${Pages.GAS_DISTRIBUTION_STATIONS}/:stationId/${Pages.GAS_THREADS}/:gasThreadId`,
    element: <GasDistributionStationGasThread />,
    children: gasDistributionStationGasThreadRoutes,
    handle: {
      permissions: UserPermissions.GasDistributionStationThreadRead,
    },
  },

  {
    path: `${Pages.GAS_DISTRIBUTION_STATIONS}/:stationId/${Pages.GAS_THREADS}/:gasThreadId/${Pages.METERING_POINTS}/:meteringPointId`,
    element: <MeteringPointDetails />,
    children: meteringPointsRoutes,
    handle: {
      permissions: UserPermissions.GasDistributionStationThreadRead,
    },
  },

  makeAuditRoute(
    `${Pages.GAS_DISTRIBUTION_STATIONS}/:stationId/${Pages.GAS_THREADS}/:gasThreadId/${Pages.METERING_POINTS}/:meteringPointId`,
    <MeteringPointAudit />,
    UserPermissions.GasDistributionStationThreadRead,
  ),

  {
    path: `${Pages.GAS_DISTRIBUTION_STATIONS}/:stationId/${Pages.GAS_THREADS}/:gasThreadId/${Pages.METERING_POINTS}/:meteringPointId/${Pages.METERING_POINT_ACTS}/:meteringPointActId`,
    element: <MeteringPointActDetails />,
    children: meteringPointActDetailsRoutes,
    handle: {
      permissions: UserPermissions.GasDistributionStationThreadRead,
    },
  },

  {
    path: `${Pages.GAS_DISTRIBUTION_STATIONS}/:stationId/${Pages.GAS_THREADS}/:gasThreadId/${Pages.METERING_POINTS}/:meteringPointId/${Pages.METERING_POINT_ACTS}/:meteringPointActId/${Pages.METERING_POINT_ACTS_FORM}`,
    element: <MeteringPointActForm isEditMode />,
    handle: {
      permissions: UserPermissions.GasDistributionStationThreadRead,
    },
  },

  {
    path: `${Pages.GAS_DISTRIBUTION_STATIONS}/:stationId/${Pages.GAS_THREADS}/:gasThreadId/${Pages.METERING_POINTS}/:meteringPointId/${Pages.METERING_POINT_ACTS}/${Pages.METERING_POINT_ACTS_FORM}`,
    element: <MeteringPointActForm />,
    handle: {
      permissions: UserPermissions.GasDistributionStationThreadRead,
    },
  },

  {
    path: `${Pages.GAS_DISTRIBUTION_STATIONS}/:stationId/${Pages.GAS_THREADS}/:gasThreadId/${Pages.METERING_POINTS}/:meteringPointId/${Pages.METERING_NODES}/:meteringNodeId`,
    element: <NodeDetails />,
    children: meteringPointsNodeRoutes,
    handle: {
      permissions: UserPermissions.GasDistributionStationThreadRead,
    },
  },

  {
    path: `${Pages.GAS_DISTRIBUTION_STATIONS}/:stationId/${Pages.GAS_THREADS}/:gasThreadId/${Pages.SECTORS}/:sectorId`,
    element: <SectorDetails />,
    children: sectorDetailsRoutes,
    handle: {
      permissions: UserPermissions.GasDistributionStationThreadRead,
    },
  },

  {
    path: Pages.GAS_THREADS,
    element: <GasThreads />,
    handle: {
      keepAlive: true,
      permissions: UserPermissions.GasDistributionStationThreadRead,
    },
  },
  {
    path: Pages.CONTRACTS,
    element: <Contracts />,
    handle: {
      keepAlive: true,
      permissions: UserPermissions.CommercialConsumersContractRead,
    },
  },
  {
    path: `${Pages.CONTRACTS}/:contractId`,
    element: <ContractDetails />,
    children: contractDetailsRoutes,
    handle: {
      keepAlive: true,
      permissions: UserPermissions.CommercialConsumersContractRead,
    },
  },
  {
    path: `${Pages.GAS_THREADS}/:gasThreadId`,
    element: <GasThreadDetails />,
    children: gasThreadRoutes,
    handle: {
      keepAlive: true,
      permissions: UserPermissions.GasDistributionStationThreadRead,
    },
  },
  {
    path: `${Pages.GAS_THREADS}/:gasThreadId/${Pages.METERING_POINTS}/:meteringPointId`,
    element: <GasThreadMeteringPoints />,
    children: meteringPointsRoutes,
    handle: {
      permissions: UserPermissions.GasDistributionStationThreadRead,
    },
  },
  {
    path: `${Pages.GAS_THREADS}/:gasThreadId/${Pages.METERING_POINTS}/:meteringPointId/${Pages.METERING_POINT_ACTS}/:meteringPointActId`,
    element: <MeteringPointActDetails />,
    children: meteringPointActDetailsRoutes,
    handle: {
      permissions: UserPermissions.GasDistributionStationThreadRead,
    },
  },
  {
    path: `${Pages.GAS_THREADS}/:gasThreadId/${Pages.METERING_POINTS}/:meteringPointId/${Pages.METERING_POINT_ACTS}/${Pages.METERING_POINT_ACTS_FORM}`,
    element: <MeteringPointActForm />,
    handle: {
      permissions: UserPermissions.GasDistributionStationThreadRead,
    },
  },
  {
    path: `${Pages.GAS_THREADS}/:gasThreadId/${Pages.METERING_POINTS}/:meteringPointId/${Pages.METERING_POINT_ACTS}/:meteringPointActId/${Pages.METERING_POINT_ACTS_FORM}`,
    element: <MeteringPointActForm isEditMode />,
    handle: {
      permissions: UserPermissions.GasDistributionStationThreadRead,
    },
  },

  makeAuditRoute(
    `${Pages.GAS_THREADS}/:gasThreadId/${Pages.METERING_POINTS}/:meteringPointId`,
    <MeteringPointAudit />,
    UserPermissions.GasDistributionStationThreadRead,
  ),

  {
    path: `${Pages.GAS_THREADS}/:gasThreadId/${Pages.METERING_POINTS}/:meteringPointId/${Pages.METERING_NODES}/:meteringNodeId`,
    element: <NodeDetails />,
    children: meteringPointsNodeRoutes,
    handle: {
      permissions: UserPermissions.GasDistributionStationThreadRead,
    },
  },
  {
    path: `${Pages.GAS_THREADS}/:gasThreadId/${Pages.SECTORS}/:sectorId`,
    element: <SectorDetails />,
    children: sectorDetailsRoutes,
    handle: {
      permissions: UserPermissions.GasDistributionStationThreadRead,
    },
  },
  {
    path: Pages.METERING_POINTS,
    element: <MeteringPoints />,
    handle: {
      keepAlive: true,
      permissions: UserPermissions.GasDistributionStationMeteringPointRead,
    },
  },
  {
    path: `${Pages.METERING_POINTS}/:meteringPointId`,
    element: <MeteringPointDetails />,
    children: meteringPointsRoutes,
    handle: {
      keepAlive: true,
      permissions: UserPermissions.GasDistributionStationMeteringPointRead,
    },
  },
  {
    path: `${Pages.METERING_POINTS}/:meteringPointId/${Pages.METERING_NODES}/:meteringNodeId`,
    element: <NodeDetails />,
    children: meteringPointsNodeRoutes,
    handle: {
      permissions: UserPermissions.GasDistributionStationGasMeteringNodeRead,
    },
  },
  {
    path: `${Pages.COMMERCIAL_METERING_POINTS_PART}/:commercialMeteringPointId/${Pages.METERING_NODES}/:meteringNodeId`,
    element: <NodeDetails />,
    children: commercialMeteringPointsNodeRoutes,
    handle: {
      permissions: UserPermissions.CommercialConsumersGasMeteringNodeRead,
    },
  },
  {
    path: Pages.SECTOR_BRIDGES,
    element: <SectorBridges />,
    handle: {
      keepAlive: true,
      permissions: UserPermissions.GasDistributionStationSectorBridgeRead,
    },
  },
  {
    path: `${Pages.SECTOR_BRIDGES}/:sectorBridgeId`,
    element: <SectorBridgeDetails />,
    children: [
      {
        path: Pages.BRIDGE_ACTS,
        element: <SectorBridgeDetailsBridgeActs />,
        children: [{ path: ':bridgeActId', element: <BridgeActDetails /> }],
        handle: {
          permissions:
            UserPermissions.GasDistributionStationSectorBridgeActRead,
        },
      },
      {
        path: '',
        element: <SectorBridgeDetailsSectors />,
      },
    ],
    handle: {
      keepAlive: true,
      permissions: UserPermissions.GasDistributionStationSectorBridgeRead,
    },
  },
  makeAuditRoute(
    `${Pages.SECTOR_BRIDGES}/:sectorBridgeId`,
    <SectorBridgeAudit />,
    UserPermissions.GasDistributionStationSectorBridgeRead,
  ),
  {
    path: `${Pages.SECTOR_BRIDGES}/:sectorBridgeId/:sectorId`,
    element: <SectorDetails />,
    children: sectorDetailsRoutes,
    handle: {
      permissions: UserPermissions.GasDistributionStationSectorBridgeRead,
    },
  },
  {
    path: Pages.BRIDGE_ACTS,
    element: <BridgeActs />,
    children: [{ path: ':bridgeActId', element: <BridgeActDetails /> }],
    handle: {
      permissions: UserPermissions.GasDistributionStationSectorBridgeActRead,
    },
  },
  {
    path: Pages.METERING_DEVICE_CONSUMER_INDICATORS,
    element: <MeteringDeviceConsumerIndicators />,
    children: [
      {
        path: ':meteringDeviceConsumerIndicatorId',
        element: <div>Details placeholder</div>,
      },
    ],
    handle: {
      permissions: UserPermissions.ActualAccountingConsumerIndicatorRead,
    },
  },
  {
    path: Pages.COUNTERPARTIES,
    element: <Counterparties />,
    handle: {
      keepAlive: true,
      permissions: UserPermissions.CommercialConsumersCounterpartyRead,
    },
  },
  {
    path: `${Pages.COUNTERPARTIES}/:counterpartId`,
    element: <CounterpartDetails />,
    children: [
      {
        path: '',
        element: <CounterpartInformation />,
      },
      {
        path: `${Pages.COMMERCIAL_METERING_POINTS_PART}`,
        element: <CommercialMeteringPointsView />,
      },
      {
        path: `${Pages.CONTRACTS}`,
        element: <CounterpartContracts />,
        handle: {
          permissions: UserPermissions.CommercialConsumersContractRead,
        },
      },
      {
        path: `${Pages.REQUESTS}`,
        element: <CounterpartRequests />,
        children: [
          {
            path: `:requestId`,
            element: <InfoRequestForm />,
          },
        ],
      },
      {
        path: `${Pages.DOCUMENTS_AND_ACTS}/`,
        element: <CounterpartDocuments />,
      },
    ],
    handle: {
      keepAlive: true,
      permissions: UserPermissions.CommercialConsumersCounterpartyRead,
    },
  },

  makeAuditRoute(
    `${Pages.COUNTERPARTIES}/:counterpartId`,
    <CounterpartyAudit />,
    UserPermissions.CommercialConsumersCounterpartyRead,
  ),

  {
    path: `${Pages.COUNTERPARTIES}/:counterpartId/${Pages.DOCUMENTS_AND_ACTS}/:documentId`,
    element: <DocumentDetails />,
    handle: {
      permissions: UserPermissions.CommercialConsumersCounterpartyRead,
    },
  },
  {
    path: `${Pages.COUNTERPARTIES}/:counterpartId/${Pages.COMMERCIAL_METERING_POINTS_PART}/:commercialMeteringPointId`,
    element: <CommercialMeteringPointDetails />,
    children: commercialMeteringPointDetailsRoutes,
    handle: {
      permissions: UserPermissions.CommercialConsumersCounterpartyRead,
    },
  },
  {
    path: `${Pages.COUNTERPARTIES}/:counterpartId/${Pages.COMMERCIAL_METERING_POINTS_PART}/:commercialMeteringPointId/${Pages.METERING_NODES}/:meteringNodeId`,
    element: <NodeDetails />,
    children: commercialMeteringPointsNodeRoutes,
    handle: {
      permissions: UserPermissions.CommercialConsumersCounterpartyRead,
    },
  },
  {
    path: `${Pages.COUNTERPARTIES}/:counterpartId/${Pages.CONTRACTS}/:contractId`,
    element: <ContractDetails />,
    children: contractDetailsRoutes,
    handle: {
      permissions: UserPermissions.CommercialConsumersCounterpartyRead,
    },
  },
  makeAuditRoute(
    `${Pages.COUNTERPARTIES}/:counterpartId/${Pages.CONTRACTS}/:contractId`,
    <ContractAudit />,
    UserPermissions.GasDistributionStationMeteringPointRead,
  ),
  {
    path: `${Pages.COUNTERPARTIES}/:counterpartId/${Pages.COMMERCIAL_METERING_POINTS_PART}/:commercialMeteringPointId/${Pages.OWNERSHIP_HISTORY}`,
    element: <CommercialMeteringPointOwnerships />,
    children: [
      {
        path: ':ownershipId',
        element: <CommercialMeteringPointOwnershipsFormView />,
      },
    ],
    handle: {
      permissions: UserPermissions.CommercialConsumersCounterpartyRead,
    },
  },
  {
    path: Pages.GAS_ROUTES,
    element: <GasRoutes />,
    children: [
      {
        path: '',
        element: <GRoutes />,
        children: [
          {
            path: ':gasRouteId',
            element: <GasRouteDetails />,
          },
        ],
      },
      { path: Pages.OPERATING_GAS_ROUTES_PART, element: <OperatingRoutes /> },
    ],
    handle: {
      keepAlive: true,
      permissions: UserPermissions.GasDistributionStationGasRouteRead,
    },
  },
  {
    path: `${getOperatingGasRoutesURL()}/${Pages.CREATE_PART}`,
    element: <OperatingRouteDetails type={DialogType.Create} />,
  },
  {
    path: `${Pages.GAS_ROUTES}/${Pages.OPERATING_GAS_ROUTES_PART}/:operatingGasRouteId`,
    element: <OperatingRouteDetails />,
    handle: {
      permissions: UserPermissions.GasDistributionStationGasRouteRead,
    },
  },
  makeAuditRoute(
    `${Pages.GAS_ROUTES}/${Pages.OPERATING_GAS_ROUTES_PART}/:operatingGasRouteId`,
    <OperatingGasRouteAudit />,
    UserPermissions.GasDistributionStationGasRouteRead,
  ),
  {
    path: Pages.COMMERCIAL_METERING_POINTS_PART,
    element: <CommercialMeteringPoints />,
    handle: {
      keepAlive: true,
      permissions:
        UserPermissions.CommercialConsumersCommercialMeteringPointRead,
    },
  },
  {
    path: `${Pages.COMMERCIAL_METERING_POINTS_PART}/:commercialMeteringPointId`,
    element: <CommercialMeteringPointDetails />,
    children: commercialMeteringPointDetailsRoutes,
    handle: {
      keepAlive: true,
      permissions:
        UserPermissions.CommercialConsumersCommercialMeteringPointRead,
    },
  },
  {
    path: `${Pages.COMMERCIAL_METERING_POINTS_PART}/:commercialMeteringPointId/${Pages.OWNERSHIP_HISTORY}`,
    element: <CommercialMeteringPointOwnerships />,
    children: [
      {
        path: ':ownershipId',
        element: <CommercialMeteringPointOwnershipsFormView />,
      },
    ],
  },
  {
    path: `${Pages.FORM_NINE}`,
    element: <FormNine />,
    handle: {
      keepAlive: true,
      permissions:
        UserPermissions.OperationalAccountingConsumersWithoutNominationRead,
    },
  },
  {
    path: `${Pages.FORM_NINE}/:formId`,
    element: <FormNineDetails />,
    handle: {
      permissions:
        UserPermissions.OperationalAccountingConsumersWithoutNominationRead,
    },
  },
  makeAuditRoute(
    `${Pages.FORM_NINE}/:formId`,
    <FormNineAudit />,
    UserPermissions.OperationalAccountingConsumersWithoutNominationRead,
  ),
  {
    path: Pages.SECTORS,
    element: <Sectors />,
    handle: {
      keepAlive: true,
      permissions: UserPermissions.GasDistributionStationSectorRead,
    },
  },
  {
    path: `${Pages.SECTORS}/:sectorId`,
    element: <SectorDetails />,
    children: sectorDetailsRoutes,
    handle: {
      permissions: UserPermissions.GasDistributionStationSectorRead,
    },
  },
  {
    path: `${Pages.OPERATIONAL_INCOMES}`,
    element: (
      <Navigate to={dayjs().add(-1, 'day').format(ISO_DATE_FORMAT)} replace />
    ),
    handle: {
      permissions: UserPermissions.OperationalAccountingOperationalIncomeRead,
    },
  },
  {
    path: `${Pages.OPERATIONAL_INCOMES}/:date`,
    element: <OperationalIncomes />,
    handle: {
      keepAlive: true,
      permissions: UserPermissions.OperationalAccountingOperationalIncomeRead,
    },
  },
  {
    path: `${Pages.OPERATIONAL_INCOMES}/:date/:operationalIncomeId`,
    element: <OperationalIncomeDetails />,
    children: [
      {
        path: `:dailyIncomeId`,
        element: <OperationalIncomeCosts />,
      },
    ],
    handle: {
      permissions: UserPermissions.OperationalAccountingOperationalIncomeRead,
    },
  },
  makeAuditRoute(
    `${Pages.OPERATIONAL_INCOMES}/:date/:operationalIncomeId`,
    <OperationalIncomeAudit />,
    UserPermissions.OperationalAccountingOperationalIncomeRead,
  ),
  {
    path: Pages.ACTUAL_INCOMES,
    element: (
      <Navigate
        to={dayjs().subtract(1, 'month').endOf('month').format(ISO_DATE_FORMAT)}
        replace
      />
    ),
    handle: {
      permissions: UserPermissions.ActualAccountingActualIncomeRead,
    },
  },
  {
    path: `${Pages.ACTUAL_INCOMES}/:date`,
    element: <ActualIncomes />,
    handle: {
      keepAlive: true,
      permissions: UserPermissions.ActualAccountingActualIncomeRead,
    },
  },
  {
    path: `${Pages.ACTUAL_INCOMES}/:date/:meteringPointId`,
    element: <ActualIncomeDetails />,
    children: actualIncomeDetailsRoutes,
    handle: {
      permissions: UserPermissions.ActualAccountingActualIncomeRead,
    },
  },
  makeAuditRoute(
    `${Pages.ACTUAL_INCOMES}/:date/:meteringPointId`,
    <ActualIncomeAudit />,
    UserPermissions.ActualAccountingActualIncomeRead,
  ),
  {
    path: `${Pages.OPERATIONAL_EXPENSES}`,
    element: <Navigate to={dayjs().format(ISO_DATE_FORMAT)} replace />,
    handle: {
      permissions: UserPermissions.OperationalAccountingOperationalExpenseRead,
    },
  },
  {
    path: `${Pages.OPERATIONAL_EXPENSES}/:date`,
    element: <OperationalExpenses />,
    handle: {
      keepAlive: true,
      permissions: UserPermissions.OperationalAccountingOperationalExpenseRead,
    },
  },
  {
    path: `${Pages.OPERATIONAL_EXPENSES}/:date/:operationalExpensesId`,
    element: <OperationalExpenseDetails />,
    children: [
      { path: ':meteringPointId', element: <OperationalExpensesForm /> },
    ],
    handle: {
      permissions: UserPermissions.OperationalAccountingOperationalExpenseRead,
    },
  },
  makeAuditRoute(
    () =>
      `${Pages.OPERATIONAL_EXPENSES}/:date/:operationalExpensesId/${Pages.AUDIT}/:operationalExpensesMeteringPointId`,
    <OperationalExpenseAudit />,
    UserPermissions.OperationalAccountingOperationalExpenseRead,
  ),
  {
    path: `${Pages.METERING_POINTS}/:meteringPointId/${Pages.METERING_POINT_ACTS}/${Pages.METERING_POINT_ACTS_FORM}`,
    element: <MeteringPointActForm />,
    handle: {
      permissions: UserPermissions.GasDistributionStationMeteringPointRead,
    },
  },
  {
    path: `${Pages.COMMERCIAL_METERING_POINTS_PART}/:commercialMeteringPointId/${Pages.DOCUMENTS_AND_ACTS}/${Pages.ACT_BALANCE_OWNERSHIP}/form`,
    element: <CommercialMeteringPointBalanceActForm />,
  },
  {
    path: `${Pages.COMMERCIAL_METERING_POINTS_PART}/:commercialMeteringPointId/${Pages.DOCUMENTS_AND_ACTS}/${Pages.ACT_BALANCE_OWNERSHIP}/:balanceActId/form`,
    element: <CommercialMeteringPointBalanceActForm isEditMode />,
  },
  {
    path: `${Pages.COMMERCIAL_METERING_POINTS_PART}/:commercialMeteringPointId/${Pages.DOCUMENTS_AND_ACTS}/${Pages.ACT_BALANCE_OWNERSHIP}/:balanceActId`,
    element: <CommercialMeteringPointBalanceActDetails />,
  },
  makeAuditRoute(
    `${Pages.COMMERCIAL_METERING_POINTS_PART}/:commercialMeteringPointId`,
    <CommercialMeteringPointAudit />,
  ),
  makeAuditRoute(
    `${Pages.METERING_POINTS}/:meteringPointId`,
    <MeteringPointAudit />,
  ),
  makeAuditRoute(
    `${Pages.CONTRACTS}/:contractId`,
    <ContractAudit />,
    UserPermissions.GasDistributionStationMeteringPointRead,
  ),
  {
    path: `${Pages.COUNTERPARTIES}/:counterpartId/${Pages.COMMERCIAL_METERING_POINTS_PART}/:commercialMeteringPointId/${Pages.DOCUMENTS_AND_ACTS}/${Pages.ACT_BALANCE_OWNERSHIP}/form`,
    element: <CommercialMeteringPointBalanceActForm />,
  },
  {
    path: `${Pages.COUNTERPARTIES}/:counterpartId/${Pages.COMMERCIAL_METERING_POINTS_PART}/:commercialMeteringPointId/${Pages.DOCUMENTS_AND_ACTS}/${Pages.ACT_BALANCE_OWNERSHIP}/:balanceActId/form`,
    element: <CommercialMeteringPointBalanceActForm isEditMode />,
  },
  {
    path: `${Pages.COUNTERPARTIES}/:counterpartId/${Pages.COMMERCIAL_METERING_POINTS_PART}/:commercialMeteringPointId/${Pages.DOCUMENTS_AND_ACTS}/${Pages.ACT_BALANCE_OWNERSHIP}/:balanceActId`,
    element: <CommercialMeteringPointBalanceActDetails />,
  },

  {
    path: `${Pages.COMMERCIAL_METERING_POINTS_PART}/:commercialMeteringPointId/${Pages.DOCUMENTS_AND_ACTS}/${Pages.TECHNICAL_CONDITIONS_DOCUMENT}/form`,
    element: <TechnicalConditionsDocumentForm />,
  },
  {
    path: `${Pages.COMMERCIAL_METERING_POINTS_PART}/:commercialMeteringPointId/${Pages.DOCUMENTS_AND_ACTS}/${Pages.TECHNICAL_CONDITIONS_DOCUMENT}/:technicalConditionsDocumentId/form`,
    element: <TechnicalConditionsDocumentForm isEditMode />,
  },
  {
    path: `${Pages.COMMERCIAL_METERING_POINTS_PART}/:commercialMeteringPointId/${Pages.DOCUMENTS_AND_ACTS}/${Pages.TECHNICAL_CONDITIONS_DOCUMENT}/:technicalConditionsDocumentId`,
    element: <CommercialMeteringPointTechnicalConditionsDocumentDetails />,
  },

  {
    path: `${Pages.COUNTERPARTIES}/:counterpartId/${Pages.COMMERCIAL_METERING_POINTS_PART}/:commercialMeteringPointId/${Pages.DOCUMENTS_AND_ACTS}/${Pages.TECHNICAL_CONDITIONS_DOCUMENT}/form`,
    element: <TechnicalConditionsDocumentForm />,
  },
  {
    path: `${Pages.COUNTERPARTIES}/:counterpartId/${Pages.COMMERCIAL_METERING_POINTS_PART}/:commercialMeteringPointId/${Pages.DOCUMENTS_AND_ACTS}/${Pages.TECHNICAL_CONDITIONS_DOCUMENT}/:technicalConditionsDocumentId/form`,
    element: <TechnicalConditionsDocumentForm isEditMode />,
  },
  {
    path: `${Pages.COUNTERPARTIES}/:counterpartId/${Pages.COMMERCIAL_METERING_POINTS_PART}/:commercialMeteringPointId/${Pages.DOCUMENTS_AND_ACTS}/${Pages.TECHNICAL_CONDITIONS_DOCUMENT}/:technicalConditionsDocumentId`,
    element: <CommercialMeteringPointTechnicalConditionsDocumentDetails />,
  },

  {
    path: `${Pages.COMMERCIAL_METERING_POINTS_PART}/:commercialMeteringPointId/${Pages.DOCUMENTS_AND_ACTS}/${Pages.DOCUMENT_PROJECT}/form`,
    element: <MeteringPointProjectForm />,
  },
  {
    path: `${Pages.COMMERCIAL_METERING_POINTS_PART}/:commercialMeteringPointId/${Pages.DOCUMENTS_AND_ACTS}/${Pages.DOCUMENT_PROJECT}/:projectId/form`,
    element: <MeteringPointProjectForm />,
  },
  {
    path: `${Pages.COMMERCIAL_METERING_POINTS_PART}/:commercialMeteringPointId/${Pages.DOCUMENTS_AND_ACTS}/${Pages.DOCUMENT_PROJECT}/:projectId`,
    element: <CommercialMeteringPointProjectDetails />,
  },
  {
    path: `${Pages.COUNTERPARTIES}/:counterpartId/${Pages.COMMERCIAL_METERING_POINTS_PART}/:commercialMeteringPointId/${Pages.DOCUMENTS_AND_ACTS}/${Pages.DOCUMENT_PROJECT}/form`,
    element: <MeteringPointProjectForm />,
  },
  {
    path: `${Pages.COUNTERPARTIES}/:counterpartId/${Pages.COMMERCIAL_METERING_POINTS_PART}/:commercialMeteringPointId/${Pages.DOCUMENTS_AND_ACTS}/${Pages.DOCUMENT_PROJECT}/:projectId/form`,
    element: <MeteringPointProjectForm />,
  },
  {
    path: `${Pages.COUNTERPARTIES}/:counterpartId/${Pages.COMMERCIAL_METERING_POINTS_PART}/:commercialMeteringPointId/${Pages.DOCUMENTS_AND_ACTS}/${Pages.DOCUMENT_PROJECT}/:projectId`,
    element: <CommercialMeteringPointProjectDetails />,
  },

  {
    path: `${Pages.COMMERCIAL_METERING_POINTS_PART}/:commercialMeteringPointId/${Pages.DOCUMENTS_AND_ACTS}/${Pages.GUARANTEE_LETTER}/form`,
    element: <GuaranteeLetterForm />,
  },
  {
    path: `${Pages.COMMERCIAL_METERING_POINTS_PART}/:commercialMeteringPointId/${Pages.DOCUMENTS_AND_ACTS}/${Pages.GUARANTEE_LETTER}/:letterId/form`,
    element: <GuaranteeLetterForm isEditMode />,
  },
  {
    path: `${Pages.COMMERCIAL_METERING_POINTS_PART}/:commercialMeteringPointId/${Pages.DOCUMENTS_AND_ACTS}/${Pages.GUARANTEE_LETTER}/:letterId`,
    element: <CommercialMeteringPointGuaranteeLetterDetails />,
  },

  {
    path: `${Pages.COUNTERPARTIES}/:counterpartId/${Pages.COMMERCIAL_METERING_POINTS_PART}/:commercialMeteringPointId/${Pages.DOCUMENTS_AND_ACTS}/${Pages.GUARANTEE_LETTER}/form`,
    element: <GuaranteeLetterForm />,
  },
  {
    path: `${Pages.COUNTERPARTIES}/:counterpartId/${Pages.COMMERCIAL_METERING_POINTS_PART}/:commercialMeteringPointId/${Pages.DOCUMENTS_AND_ACTS}/${Pages.GUARANTEE_LETTER}/:letterId/form`,
    element: <GuaranteeLetterForm isEditMode />,
  },
  {
    path: `${Pages.COUNTERPARTIES}/:counterpartId/${Pages.COMMERCIAL_METERING_POINTS_PART}/:commercialMeteringPointId/${Pages.DOCUMENTS_AND_ACTS}/${Pages.GUARANTEE_LETTER}/:letterId`,
    element: <CommercialMeteringPointGuaranteeLetterDetails />,
  },

  {
    path: `${Pages.METERING_POINTS}/:meteringPointId/${Pages.METERING_POINT_ACTS}/:meteringPointActId`,
    element: <MeteringPointActDetails />,
    children: meteringPointActDetailsRoutes,
    handle: {
      permissions: UserPermissions.GasDistributionStationMeteringPointRead,
    },
  },
  {
    path: `${Pages.METERING_POINTS}/:meteringPointId/${Pages.METERING_POINT_ACTS}/:meteringPointActId/${Pages.METERING_POINT_ACTS_FORM}`,
    element: <MeteringPointActForm isEditMode />,
    handle: {
      permissions: UserPermissions.GasDistributionStationMeteringPointRead,
    },
  },
  /*** Commercial Metering Point Acts **/
  {
    path: `${Pages.COMMERCIAL_METERING_POINTS_PART}/:commercialMeteringPointId/${Pages.METERING_POINT_ACTS}/${Pages.METERING_POINT_ACTS_FORM}`,
    element: <CommercialMeteringPointActForm />,
  },
  {
    path: `${Pages.COMMERCIAL_METERING_POINTS_PART}/:commercialMeteringPointId/${Pages.METERING_POINT_ACTS}/:meteringPointActId`,
    element: <MeteringPointActDetails />,
    children: meteringPointActDetailsRoutes,
  },
  {
    path: `${Pages.COMMERCIAL_METERING_POINTS_PART}/:commercialMeteringPointId/${Pages.METERING_POINT_ACTS}/:meteringPointActId/${Pages.METERING_POINT_ACTS_FORM}`,
    element: <CommercialMeteringPointActForm isEditMode />,
  },
  {
    path: `${Pages.COUNTERPARTIES}/:counterpartId/${Pages.COMMERCIAL_METERING_POINTS_PART}/:commercialMeteringPointId/${Pages.METERING_POINT_ACTS}/${Pages.METERING_POINT_ACTS_FORM}`,
    element: <CommercialMeteringPointActForm />,
  },
  {
    path: `${Pages.COUNTERPARTIES}/:counterpartId/${Pages.COMMERCIAL_METERING_POINTS_PART}/:commercialMeteringPointId/${Pages.METERING_POINT_ACTS}/:meteringPointActId`,
    element: <MeteringPointActDetails />,
    children: meteringPointActDetailsRoutes,
  },
  {
    path: `${Pages.COUNTERPARTIES}/:counterpartId/${Pages.COMMERCIAL_METERING_POINTS_PART}/:commercialMeteringPointId/${Pages.METERING_POINT_ACTS}/:meteringPointActId/${Pages.METERING_POINT_ACTS_FORM}`,
    element: <CommercialMeteringPointActForm isEditMode />,
  },
  /*** Violation Acts ***/
  {
    path: `${Pages.COMMERCIAL_METERING_POINTS_PART}/:commercialMeteringPointId/${Pages.VIOLATION_ACTS}/form`,
    element: <ViolationActForm />,
  },
  {
    path: `${Pages.COMMERCIAL_METERING_POINTS_PART}/:commercialMeteringPointId/${Pages.VIOLATION_ACTS}/:violationActId/form`,
    element: <ViolationActForm isEditMode />,
  },
  {
    path: `${Pages.COMMERCIAL_METERING_POINTS_PART}/:commercialMeteringPointId/${Pages.VIOLATION_ACTS}/:violationActId`,
    element: <ViolationActDetails />,
    children: violationActDetailsRoutes,
  },

  {
    path: `${Pages.COUNTERPARTIES}/:counterpartId/${Pages.COMMERCIAL_METERING_POINTS_PART}/:commercialMeteringPointId/${Pages.VIOLATION_ACTS}/form`,
    element: <ViolationActForm />,
  },
  {
    path: `${Pages.COUNTERPARTIES}/:counterpartId/${Pages.COMMERCIAL_METERING_POINTS_PART}/:commercialMeteringPointId/${Pages.VIOLATION_ACTS}/:violationActId/form`,
    element: <ViolationActForm isEditMode />,
  },
  {
    path: `${Pages.COUNTERPARTIES}/:counterpartId/${Pages.COMMERCIAL_METERING_POINTS_PART}/:commercialMeteringPointId/${Pages.VIOLATION_ACTS}/:violationActId`,
    element: <ViolationActDetails />,
    children: violationActDetailsRoutes,
  },

  {
    path: `${Pages.MONTHLY_PCP}`,
    element: (
      <Navigate
        to={dayjs().subtract(1, 'month').endOf('month').format(ISO_DATE_FORMAT)}
        replace
      />
    ),
    handle: {
      permissions:
        UserPermissions.ActualAccountingGasThreadPhysicoChemicalIndicatorRead,
    },
  },
  {
    path: `${Pages.MONTHLY_PCP}/:date`,
    element: <MonthlyPCP />,
    handle: {
      keepAlive: true,
      permissions:
        UserPermissions.ActualAccountingGasThreadPhysicoChemicalIndicatorRead,
    },
  },
  {
    path: `${Pages.MONTHLY_PCP}/:date/:monthlyPhysicalChemicalParameterId`,
    element: <MonthlyPCPDetails />,
    children: [
      {
        path: `${Pages.TRUNK_ROUTE}/:treadId`,
        element: <HigherCalorificValueValueFormForGasTread />,
      },
      {
        path: `${Pages.MIXED_ROUTE}/:gdsId`,
        element: <HigherCalorificValueValueFormForGDS />,
      },
    ],
    handle: {
      permissions:
        UserPermissions.ActualAccountingGasThreadPhysicoChemicalIndicatorRead,
    },
  },
  makeAuditRoute(
    `${Pages.MONTHLY_PCP}/:date/:monthlyPhysicalChemicalParameterId`,
    <MonthlyPCPAudit />,
    UserPermissions.ActualAccountingGasThreadPhysicoChemicalIndicatorRead,
  ),
  {
    path: `${Pages.ACTUAL_EXPENSES}`,
    element: (
      <Navigate
        to={dayjs().subtract(1, 'month').endOf('month').format(ISO_DATE_FORMAT)}
        replace
      />
    ),
    handle: {
      permissions: UserPermissions.ActualAccountingActualExpenseRead,
    },
  },
  {
    path: `${Pages.ACTUAL_EXPENSES}/:date`,
    element: <ActualExpensesCounterparties />,
    handle: {
      keepAlive: true,
      permissions: UserPermissions.ActualAccountingActualExpenseRead,
    },
  },
  {
    path: `${Pages.ACTUAL_EXPENSES}/:date/:counterpartyId/:contractId`,
    element: <ActualExpensesCounterpartDetails />,
    handle: {
      keepAlive: true,
      permissions: UserPermissions.ActualAccountingActualExpenseRead,
    },
  },
  {
    path: `${Pages.ACTUAL_EXPENSES}/:date/:counterpartyId/:contractId/:commercialMeteringPointId`,
    element: <ActualExpensesMeteringPointDetails />,
    children: actualExpensesDetailsRoutes,
    handle: {
      permissions: UserPermissions.ActualAccountingActualExpenseRead,
    },
  },
  {
    path: `${Pages.ANNUAL_ORDERED_CAPACITY}`,
    element: <Navigate to={dayjs().year().toString()} replace />,
  },
  {
    path: `${Pages.ANNUAL_ORDERED_CAPACITY}/:year`,
    element: <AnnualOrderedCapacity />,
    handle: {
      keepAlive: true,
      permissions: UserPermissions.ActualAccountingAnnualOrderedCapacityRead,
    },
  },
  {
    path: `${Pages.ANNUAL_ORDERED_CAPACITY}/:year/details/:orderedCapacityId`,
    element: <OrderedCapacityCounterpartyDetails />,
    handle: {
      permissions: UserPermissions.ActualAccountingAnnualOrderedCapacityRead,
    },
  },
  {
    path: `${Pages.CONSUMER_DOCUMENTS}`,
    element: <ConsumerDocuments />,
  },
  {
    path: `${Pages.REPORTS_LIST}`,
    element: <Navigate to='all' replace />,
    handle: {
      permissions: UserPermissions.ReportsAndAnalyticsReportRead,
    },
  },
  {
    path: `${Pages.REPORTS_LIST}/:reportTab`,
    element: <Reports />,
    handle: {
      permissions: UserPermissions.ReportsAndAnalyticsReportRead,
    },
  },
  {
    path: `${Pages.REPORTS}/constructor`,
    element: <ReportConstructor />,
  },
  {
    path: `${Pages.REPORTS}/constructor/:reportTemplateId`,
    element: <ReportConstructor />,
  },
  {
    path: `${Pages.REPORTS}/:formType`,
    element: <ReportHistory />,
  },
  {
    path: `${Pages.ORDERED_CAPACITY_EXCESSES}`,
    element: (
      <Navigate
        to={dayjs().subtract(1, 'month').endOf('month').format(ISO_DATE_FORMAT)}
        replace
      />
    ),
    handle: {
      permissions: UserPermissions.ActualAccountingOrderedCapacityExcessRead,
    },
  },
  {
    path: `${Pages.ORDERED_CAPACITY_EXCESSES}/:date`,
    element: <OrderedCapacityExcesses />,
    handle: {
      keepAlive: true,
      permissions: UserPermissions.ActualAccountingOrderedCapacityExcessRead,
    },
  },
  {
    path: `${Pages.ORDERED_CAPACITY_EXCESSES}/:date/:orderedCapacityExcessId`,
    element: <OrderedCapacityExcessDetails />,
    handle: {
      permissions: UserPermissions.ActualAccountingOrderedCapacityExcessRead,
    },
  },
  {
    path: `${Pages.NATURAL_GAS_DISTRIBUTION}`,
    element: <NaturalGasDistribution />,
    handle: {
      permissions: UserPermissions.ReportingAndAnalyticsGasDistributionRead,
    },
  },
  // Сторінка Моніторинг стану об'єктів
  {
    path: `${Pages.MONITORING_DASHBOARD}`,
    element: <MonitoringDashboard />,
    handle: {
      permissions: UserPermissions.ReportingAndAnalyticsMonitoringRead,
    },
  },
  {
    path: `${Pages.EXPIRED_DEVICES}`,
    element: <ExpiredDevices />,
    handle: {
      permissions: UserPermissions.ReportingAndAnalyticsMonitoringRead,
    },
  },
  {
    path: `${Pages.METERING_POINTS_TO_DISCONNECT}`,
    element: <MeteringPointsToDisconnect />,
    handle: {
      permissions: UserPermissions.ReportingAndAnalyticsMonitoringRead,
    },
  },
  {
    path: `${Pages.METERING_POINTS_TO_VERIFY}`,
    element: <MeteringPointsToVerify />,
    handle: {
      permissions: UserPermissions.ReportingAndAnalyticsMonitoringRead,
    },
  },
  {
    path: `${Pages.FORM_TEN}`,
    element: <FormTen />,
    handle: {
      permissions: UserPermissions.OperationalAccountingLastResortSupplierRead,
    },
  },
  {
    path: `${Pages.FORM_TEN}/:formId`,
    element: <FormTenDetails />,
    handle: {
      permissions: UserPermissions.OperationalAccountingLastResortSupplierRead,
    },
  },
  {
    path: `${Pages.ORDERED_CAPACITY_FINAL_EXCESSES}`,
    element: <Navigate to={dayjs().year().toString()} replace />,
  },
  {
    path: `${Pages.ORDERED_CAPACITY_FINAL_EXCESSES}/:year`,
    element: <OrderedCapacityFinalExcess />,
    handle: {
      keepAlive: true,
      permissions: UserPermissions.ActualAccountingOrderedCapacityExcessRead,
    },
  },
  {
    path: `${Pages.ORDERED_CAPACITY_FINAL_EXCESSES}/:year/:orderedCapacityFinalExcessId`,
    element: <OrderedCapacityFinalExcessDetails />,
    handle: {
      permissions: UserPermissions.ActualAccountingOrderedCapacityExcessRead,
    },
  },
  { path: '*', element: <NotFound /> },
  { path: Pages.NOT_FOUND, element: <NotFound /> },
];

export const allRoutes = [
  {
    path: '/',
    element: <Navigate to={Pages.AUTH_CALLBACK} replace />,
  },
  {
    path: Pages.AUTH_CALLBACK,
    element: <AuthCallback />,
  },
  {
    path: Pages.NO_PERMISSIONS,
    element: <NoPermissions />,
  },
  {
    path: Pages.NOT_AUTHORIZED,
    element: <NotAuthorizedView />,
  },
  {
    path: '/:contextOrganizationId/*',
    element: <Router />,
    children: routes,
  },
  { path: '*', element: <NotFound /> },
  { path: Pages.NOT_FOUND, element: <NotFound /> },
];

export const getRoutes = () =>
  createBrowserRouter(
    allRoutes.map(route => ({ ...route, errorElement: <Error /> })),
  );
